<template lang="html">
  <div class="custom__select center w-full">
    <VsSelect ref="selectBox" :typeable="autocomplete" :disabled="disabled" @clearValue="clearVal"
      @input-change="inputChanged" class="w-full" :placeholder="placeholder" v-model="inputVal"
      @scrollEnd="handleScrollEnd" :loader="processing || loading" :clearable="clearable" :multiple="multiple"
      @focus="handleFocus">
      <vs-select-item v-if="newLabel" :text="newLabel" value="newItemAddEvent" class="select-head-btn" />
      <vs-select-item
        v-for="item in vOptions"
        :key="'opt-' + String(item[val] || item['value'])"
        :text="item[label] || item.label"
        :value="item[val] || item['value']"
      />
    </VsSelect>
  </div>
</template>

<script>
import VsSelect from '@/components/custom/form-elements/CustomSelect.vue'
import VsSelectItem from '@/components/custom/form-elements/vsSelectItem.vue'

export default {
  name: 'Select2',

  components: {
    VsSelect,
    VsSelectItem
  },

  props: {
    ssr: {
      type: Boolean,
      default: false
    },
    selectName: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    extra: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    value: {
      default: null
    },
    val: {
      type: String,
      default: '_id'
    },
    action: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    newLabel: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: Boolean,
      default: true
    },
    searchFromStart: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    viewRendered: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      limit: 10,
      page: 1,
      search: null,
      total: 0,
      sso: [],
      processing: false,
      type: 'dropdown',
      nodata: false,
      isDropdownOpen: false
    }
  },

  async mounted() {
    if (!this.action) {
      this.sso = []
    }
    await this.getOptions()
    window.addEventListener('scroll', this.handleWindowScroll, true)
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleWindowScroll, true)
  },
  computed: {
    inputVal: {
      get() {
        if (this.multiple) {
          const values = Array.isArray(this.value) ? this.value : []
          const seen = new Set()
          const filteredArray = values.reduce((acc, v) => {
            const id = (typeof v === 'object' && v !== null) ? (v[this.val] || v['value']) : v
            const key = String(id)
            if (!key || seen.has(key)) {
              return acc
            }
            seen.add(key)
            acc.push(id)
            return acc
          }, [])

          return filteredArray
        } else {
          const id = (typeof this.value === 'object' && this.value !== null) ? (this.value[this.val] || this.value['value']) : this.value
          const option = this.vOptions.find((item) => (item[this.val] || item['value']) === id)

          return option ? option[this.val] || option['value'] : id || ''
        }
      },
      set(val) {
        if (val === 'newItemAddEvent') {
          this.$emit('addNew')
        } else {
          if (this.multiple) {
            const selectedItems = this.vOptions.filter((item) => val.includes(item[this.val] || item['value']))
            this.$emit('input', { items: selectedItems, value: val })
          } else {
            this.$emit('input', { ...this.vOptions.find((item) => val === (item[this.val] || item['value'])), value: val })
          }
        }
      }
    },

    vOptions() {
      let options = [...this.extra, ...this.options, ...this.sso]
      if (this.autocomplete && this.searchFromStart) {
        options = options.filter((e) => {
          return !this.search || (e.label || '').toLowerCase().startsWith(this.search.toLowerCase())
          // return this.search ? (e.label || '').toLowerCase().search(this.search.toLowerCase()) > -1 : true
        })

        if (this.value) {
          const tIndex = options.findIndex((x) => (x[this.val] || x['value']) === this.value)
          if (tIndex > -1) {
            options = this.array_move(options, tIndex, 0)
          }
        }
      }

      // Ensure selected values are always in the options list even if not in current search/pagination results
      if (this.multiple && Array.isArray(this.value)) {
        const seenIds = new Set()
        this.value.forEach((v) => {
          const id = typeof v === 'object' && v !== null ? v[this.val] || v['value'] : v
          const idKey = String(id)
          if (!idKey || seenIds.has(idKey)) {
            return
          }
          seenIds.add(idKey)
          const label = typeof v === 'object' && v !== null ? v.label || v.text || id : id

          if (!options.some((x) => String(x[this.val] || x['value']) === idKey)) {
            options.push({ [this.val]: id, label, value: id, text: label })
          }
        })
      } else if (!this.multiple && this.value) {
        const id = typeof this.value === 'object' && this.value !== null ? this.value[this.val] || this.value['value'] : this.value
        const label = typeof this.value === 'object' && this.value !== null ? this.value.label || this.value.text || id : id

        if (!options.some((x) => String(x[this.val] || x['value']) === String(id))) {
          options.push({ [this.val]: id, label, value: id, text: label })
        }
      }

      return this.dedupeOptionsById(options)
    },

    array_move() {
      return (arr, old_index, new_index) => {
        if (new_index >= arr.length) {
          let k = new_index - arr.length + 1
          while (k--) {
            arr.push(undefined)
          }
        }
        arr.splice(new_index, 0, arr.splice(old_index, 1)[0])
        return arr
      }
    }
  },

  methods: {
    dedupeOptionsById(options) {
      const seen = new Set()
      return (options || []).filter((entry) => {
        const id = String(entry[this.val] || entry.value || entry._id || '')
        if (!id || seen.has(id)) {
          return false
        }
        seen.add(id)
        return true
      })
    },
    handleFocus() {
      this.isDropdownOpen = true
    },

    handleWindowScroll(e) {
      const dropdown = document.querySelector('.vs-select--options')
      if (this.isDropdownOpen && this.$refs.selectBox) {
        const scrollTarget = e.target
        const isInsideDropdown = dropdown && (scrollTarget === dropdown || dropdown.contains(scrollTarget))
        const isInsideSelect = this.$el.contains(scrollTarget)
        const isInsideModal = scrollTarget.closest && (
          scrollTarget.closest('.detail-panel') ||
          scrollTarget.closest('.scrollable-modal-body') ||
          scrollTarget.closest('.vs-popup')
        )
        if (!isInsideDropdown && !isInsideSelect && !isInsideModal) {
          this.$refs.selectBox.closeOptions()
          this.isDropdownOpen = false
        }
      }
    },
    async handleScrollEnd() {
      this.$emit('scrollEnd')
      if (this.ssr && this.action && this.processing === false && !this.nodata) {
        this.page += 1
        await this.getOptions(false)
      }
    },

    async getOptions(apiStat = true) {
      const payload = {
        search: this.search,
        page: this.page,
        limit: this.limit,
        type: this.type,
        ...this.params
      }

      if (this.action && this.viewRendered) {
        this.processing = true

        this.$store
          .dispatch(this.action, payload)
          .then((response) => {
            if (apiStat) {
              this.sso = []
            }

            this.nodata = response.data.length < 1
            this.sso.push(...response.data)
          })
          .catch(() => { })
          .finally(() => {
            this.processing = false
          })
      }
    },

    inputChanged(e) {
      this.search = e.target.value
      this.page = 1
      this.$emit('search', e.target.value || '')
      this.getOptions()
    },

    clearVal() {
      this.inputVal = this.multiple ? [] : null
      this.search = null
      this.page = 1
      this.getOptions()
    }
  },

  watch: {
    action(newVal) {
      if (!newVal) {
        this.sso = []
        this.search = null
        this.page = 1
      }
      this.getOptions()
    },

    viewRendered() {
      this.getOptions()
    },

    options() {
      if (!this.action) {
        this.sso = []
      }
    }
  }
}
</script>

<style lang="scss">
.select-head-btn {
  border-radius: 0.3rem;
  background-color: #006622;

  button {
    padding-left: 0.6rem;

    &:hover {
      border-radius: 0.3rem;
      background-color: #006622;
    }

    &::before {
      content: '+';
      color: #fff;
      font-size: 1.4rem;
      line-height: 0.8;
      margin-right: 0.4rem;
    }

    span {
      color: #fff;
    }
  }
}

.spinner_circle {
  width: 20px;
  height: 20px;
  background-color: #006622;
  border-radius: 50%;
  animation: animate 1s infinite;
  transition: 0.5s;

  @keyframes animate {
    0% {
      opacity: 0.1;
      transform: scale(0.2);
    }

    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
}
</style>
